/**
 * Food suggestions while typing (#5410): asks the server's phrase index for whole search lines,
 * each with how many foods hold it, and falls back to the on-device word list when the server is
 * slow, failing or unreachable. The design is in `design-docs/food-search-suggestions.md`.
 *
 * Like `food-search-elastic.ts`, everything this module borrows arrives as a parameter rather than
 * an import, so it loads under the node test runner and its tests drive the real code: the timers,
 * the network, the sign-in state and the word list are all handed in.
 */

import { timeout } from '@cf-shared/shared-util';
import { FOOD_SUGGEST_DEADLINE_ERROR, FOOD_SUGGEST_TEXT_MAX_LENGTH, FoodSuggestResponse } from '@cf-shared/food-suggest-interface';
import { classifySearchFailure, SearchFailureOutcome } from './food-search-error';
import { decideSearchCorrection, NO_SEARCH_CORRECTION, SearchCorrection } from './food-search-correction';
import type { ElasticAuthUser, ElasticHttpResponse } from './food-search-elastic';
import type { SuggestionRow } from '@cf-shared/food-search/food-suggestion-row';

/** Suggestions start at this many typed characters. The full search keeps its own minimum of 3. */
export const SUGGEST_MIN_TEXT_LENGTH = 2;

/**
 * How long typing must pause before a suggestion request goes out. The same pause the full
 * search used: shorter would multiply the request rate, and "the app does less server work than
 * before" is what this design rests on.
 */
export const SUGGEST_DEBOUNCE_MS = 300;

/**
 * How long to wait for the server's suggestions before showing the on-device word list instead.
 * About 250 ms of network for a European user plus a 700 ms stall at the search node, with room.
 */
export const SUGGEST_FALLBACK_MS = 1200;

/**
 * How many suggestions the screen shows. The list floats over the page, so a longer list costs
 * nothing but its own height — it was 5 while the list pushed the page down. The route refuses
 * more than FOOD_SUGGEST_SIZE_MAX (10).
 */
export const SUGGEST_ROW_COUNT = 7;

/**
 * Does this text look like a barcode rather than words? The same rule the full search applies:
 * eight or more digits and no letters. Barcodes are never sent for suggestions.
 */
export function looksLikeBarcode(trimmedText: string): boolean {
  return /^\d{8,}$/.test(trimmedText) && !/[a-zA-Z]/.test(trimmedText);
}

/** Where a list of suggestions came from. */
export enum SuggestionSource {
  /** the server's phrase index: whole lines with counts */
  Server = 'server',
  /** the on-device word list: the last word finished off, no counts */
  Device = 'device',
}

export interface SuggestionList {
  source: SuggestionSource;
  /** the typed text these rows answer */
  forText: string;
  rows: SuggestionRow[];
}

/** What asking the server needs. The app passes the same sign-in pieces the full search uses. */
export interface FoodSuggestRequestDeps {
  /** resolves once Firebase has finished deciding who, if anyone, is signed in */
  authSettled: Promise<void>;
  /** how long to wait for that before giving up on the server */
  authSettledWaitMs: number;
  /** the signed-in Firebase user, or null/undefined when nobody is signed in */
  getCurrentUser: () => ElasticAuthUser | null | undefined;
  /** the origin of the backend this build is routed to */
  resolveApiOriginAsync: () => Promise<string>;
  /** issues the request; in the app this is the global fetch */
  fetchImpl: (url: string, init: { method: string; headers: Record<string, string>; body: string; signal?: AbortSignal }) => Promise<ElasticHttpResponse>;
}

/** Nobody is signed in, so the server was not asked. Not a failure: the word list answers. */
export class SignedOutError extends Error {
  constructor() {
    super('nobody is signed in, so food suggestions were not requested');
  }
}

/**
 * The server answered 504 with `FOOD_SUGGEST_DEADLINE_ERROR`: the suggest route's own 1,000 ms
 * deadline ran out before Elasticsearch answered (#6130). Not a failure: it is the server-side twin of `SUGGEST_FALLBACK_MS`, which is
 * never reported either, and the word list answers. The server logs a warning for every one, so a
 * sustained slowdown still shows there. See design-docs/food-search-suggestions.md.
 */
export class SuggestionDeadlineError extends Error {
  constructor(statusText: string) {
    super(`Food suggestions came too late: HTTP 504 ${statusText}`);
    this.name = 'SuggestionDeadlineError';
  }
}

/** False for the outcomes the word list simply covers: signed out, a passing connection blip, a late answer. */
function isWorthReporting(error: unknown): boolean {
  if (error instanceof SignedOutError || error instanceof SuggestionDeadlineError) {
    return false;
  }
  return classifySearchFailure(error) !== SearchFailureOutcome.Transient;
}

/**
 * Ask the server for suggestions for `text`. Waits for sign-in the way the full search does, and
 * refuses without a request when nobody is signed in — otherwise every pause in typing would send
 * a request the server answers 401. A 401 is retried once with a refreshed token, as the full
 * search does, because the likeliest cause is a token that went stale a moment ago.
 */
export async function requestFoodSuggestionsAsync(
  text: string, size: number, signal: AbortSignal, deps: FoodSuggestRequestDeps,
): Promise<SuggestionRow[]> {
  try {
    await timeout(deps.authSettledWaitMs, deps.authSettled);
  } catch (e) {
    // only the wait running out is expected; anything else is a real problem and goes on up
    if (e?.name !== 'TimeoutError') {
      throw e;
    }
  }
  if (!deps.getCurrentUser()) {
    throw new SignedOutError();
  }
  const apiOrigin = await deps.resolveApiOriginAsync();
  const post = async (forceRefresh: boolean) => {
    const user = deps.getCurrentUser();
    const token = user ? await user.getIdToken(forceRefresh) : null;
    return deps.fetchImpl(`${apiOrigin}/api/food-suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text, size }),
      signal,
    });
  };
  let response = await post(false);
  if (response.status === 401) {
    response = await post(true);
  }
  // only the route's own deadline names itself in the body; a 504 from a gateway or proxy in
  // front of the server has another body (or none) and stays a failure worth reporting
  if (response.status === 504) {
    let body: { error?: unknown } | null;
    try {
      body = await response.json() as { error?: unknown } | null;
    } catch (bodyError) {
      // an abort or a dropped connection while the body arrives goes on up unchanged, so the
      // caller still recognises it as a passing blip; only unreadable content becomes a failure
      if (classifySearchFailure(bodyError) === SearchFailureOutcome.Transient) {
        throw bodyError;
      }
      throw new Error(`Food suggestions failed: HTTP 504 ${response.statusText} (body unreadable)`, { cause: bodyError });
    }
    if (body?.error === FOOD_SUGGEST_DEADLINE_ERROR) {
      throw new SuggestionDeadlineError(response.statusText);
    }
  }
  if (!response.ok) {
    throw new Error(`Food suggestions failed: HTTP ${response.status} ${response.statusText}`);
  }
  return rowsFromSuggestResponse(await response.json());
}

/**
 * The rows in a server answer. A row of the wrong shape is dropped rather than trusted; an answer
 * with no suggestions list at all is an error, not an empty result.
 */
export function rowsFromSuggestResponse(response: unknown): SuggestionRow[] {
  const suggestions = (response as Partial<FoodSuggestResponse> | null)?.suggestions;
  if (!Array.isArray(suggestions)) {
    throw new Error('the food suggestion answer held no suggestions list');
  }
  const rows: SuggestionRow[] = [];
  for (const suggestion of suggestions) {
    if (typeof suggestion?.phrase === 'string' && suggestion.phrase.length > 0 && typeof suggestion.count === 'number') {
      const row: SuggestionRow = { text: suggestion.phrase, count: suggestion.count };
      // only a true flag is carried: an older server answers without the field at all, and a
      // missing field means the phrase matches what was typed
      if (suggestion.mended === true) {
        row.mended = true;
      }
      rows.push(row);
    }
  }
  return rows;
}

/**
 * Staple rows first, then the other rows (food staples plan 4b, #5624). A row that says exactly
 * what a staple row says is dropped, since both would search the same text, and the list keeps
 * to the usual number of rows.
 */
export function mergeStapleRows(stapleRows: SuggestionRow[], otherRows: SuggestionRow[]): SuggestionRow[] {
  const stapleTexts = new Set(stapleRows.map(row => row.text.toLowerCase()));
  const others = otherRows.filter(row => !stapleTexts.has(row.text.toLowerCase()));
  return [...stapleRows, ...others].slice(0, SUGGEST_ROW_COUNT);
}

/** Everything the suggestion controller borrows. */
export interface FoodSuggestControllerDeps {
  /** asks the server; rejects on any failure, with a `SignedOutError` when nobody is signed in */
  requestAsync: (text: string, size: number, signal: AbortSignal) => Promise<SuggestionRow[]>;
  /** the on-device word list's rows for `text`, worked out on the spot */
  deviceRows: (text: string) => SuggestionRow[];
  /** false when the device looks offline, so the server is not even asked */
  isLikelyOnline: () => boolean;
  /** called with every new list to show; an empty `rows` means "show nothing" */
  onList: (list: SuggestionList) => void;
  /** called when a server request starts or stops waiting, for the spinner in the search box */
  onWaitingChange: (waiting: boolean) => void;
  /** reports a real failure (not a passing connection blip, nor a late answer) to the error tracker */
  reportFailure: (error: unknown) => void;
  /**
   * The staple rows for `text` (food staples plan 4b, #5624), shown above every other row. Absent
   * while staple suggestions are switched off, and then lists are shown exactly as they arrive.
   */
  stapleRows?: (text: string) => SuggestionRow[];
  setTimer: (callback: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

/**
 * Turns keystrokes into suggestion lists.
 *
 * - Each keystroke cancels the previous pause, the previous request and its fallback timer, so an
 *   old answer can never land on newer text.
 * - The list on screen stays until a new one replaces it, so a slow answer costs a stale list,
 *   never a flicker or a frozen box.
 * - Past `SUGGEST_FALLBACK_MS`, or on any failure, the on-device word list answers instead.
 * - Text shorter than `SUGGEST_MIN_TEXT_LENGTH`, longer than the server accepts, or shaped like a
 *   barcode gets no suggestions at all.
 *
 * The page calls `cancel()` when it is left or destroyed, so nothing pending lands on a view that
 * is gone.
 */
export class FoodSuggestController {
  private debounceHandle: unknown = undefined;
  private fallbackHandle: unknown = undefined;
  /** the last list the SERVER answered, kept so a commit can reuse it instead of asking again */
  private lastServerList: SuggestionList | undefined = undefined;
  /** the in-flight correction lookup, which is cancelled by a newer one and by `cancel()` */
  private correctionAbort: AbortController | undefined = undefined;
  private abortController: AbortController | undefined = undefined;
  /** bumped on every keystroke and cancel, so a late answer can tell it is out of date */
  private generation = 0;
  /** what `onWaitingChange` last said, so it is told only when the answer changes */
  private waiting = false;

  constructor(private readonly deps: FoodSuggestControllerDeps) { }

  /** The text in the box changed: drop whatever was pending for the old text and start again. */
  textChanged(text: string) {
    // the waiting state is left alone here: a keystroke while waiting is still waiting, and
    // saying "stopped" then "started" again would flicker the spinner on every key
    this.stopPending();
    const generation = this.generation;
    const trimmed = text.trim();
    if (trimmed.length < SUGGEST_MIN_TEXT_LENGTH || looksLikeBarcode(trimmed)) {
      this.setWaiting(false);
      this.deps.onList({ source: SuggestionSource.Server, forText: text, rows: [] });
      return;
    }
    // if the device looks offline, or the text is longer than the server takes, the word list
    // answers straight away rather than after a request that cannot succeed
    if (!this.deps.isLikelyOnline() || text.length > FOOD_SUGGEST_TEXT_MAX_LENGTH) {
      this.setWaiting(false);
      this.showDeviceRows(text);
      return;
    }
    // waiting starts now, not when the request leaves after the pause: on a slow connection the
    // person may finish typing before any row arrives, and needs to see that some are coming
    this.setWaiting(true);
    this.debounceHandle = this.deps.setTimer(() => {
      this.debounceHandle = undefined;
      void this.askServerAsync(text, generation);
    }, SUGGEST_DEBOUNCE_MS);
  }

  /** Stop everything pending: the pause, the request, its fallback timer, and any correction lookup. */
  cancel() {
    this.stopPending();
    this.setWaiting(false);
  }

  /** `cancel()` without saying anything about waiting; the caller decides that. */
  private stopPending() {
    this.generation++;
    if (this.debounceHandle !== undefined) {
      this.deps.clearTimer(this.debounceHandle);
      this.debounceHandle = undefined;
    }
    this.stopRequest();
    this.abortCorrectionLookup();
  }

  /**
   * What to do about a search the person has just committed: nothing, offer a correction, or run
   * the correction instead. See `decideSearchCorrection` in `food-search-correction.ts` for the
   * rule; this method's job is only to get the rows the rule needs.
   *
   * It reuses the rows already on screen when they answer exactly this text — the ordinary case,
   * since the person watched them while typing — and asks the server otherwise, which is what
   * makes the case the owner reported work: typing `avaca`, picking the offered `avacado`, and
   * ending up on a search nothing was ever said about.
   *
   * It answers "no correction" rather than failing when the lookup cannot be made: a correction is
   * an improvement on a search that is already running, so a failure must not cost the person
   * their results. A failure worth knowing about still reaches `reportFailure`.
   */
  async correctionForAsync(text: string): Promise<SearchCorrection> {
    const trimmed = text.trim();
    if (trimmed.length < SUGGEST_MIN_TEXT_LENGTH || trimmed.length > FOOD_SUGGEST_TEXT_MAX_LENGTH || looksLikeBarcode(trimmed)) {
      return NO_SEARCH_CORRECTION;
    }
    const listed = this.lastServerList;
    // exactly this text, NOT the same text trimmed. The server picks its query from the raw text:
    // a finished last word ("avacado " with a trailing space) asks for phrases one word LONGER,
    // and those rows answer a different question. Reusing them would compare the typed line
    // against two-word phrases it can never equal, and could run a search nobody typed.
    if (listed && listed.forText === trimmed) {
      return decideSearchCorrection(trimmed, listed.rows);
    }
    if (!this.deps.isLikelyOnline()) {
      return NO_SEARCH_CORRECTION;
    }
    this.abortCorrectionLookup();
    const abortController = new AbortController();
    this.correctionAbort = abortController;
    try {
      const rows = await this.deps.requestAsync(trimmed, SUGGEST_ROW_COUNT, abortController.signal);
      // a newer lookup (or a cancel) started while this one was in flight: its answer is the
      // current one, and this answer is about text nobody is looking at any more
      if (this.correctionAbort !== abortController) {
        return NO_SEARCH_CORRECTION;
      }
      return decideSearchCorrection(trimmed, rows);
    } catch (error) {
      // signed out, a passing connection blip and a late answer are not failures worth reporting --
      // the same split the typing path makes
      if (isWorthReporting(error)) {
        this.deps.reportFailure(error);
      }
      return NO_SEARCH_CORRECTION;
    } finally {
      if (this.correctionAbort === abortController) {
        this.correctionAbort = undefined;
      }
    }
  }

  private abortCorrectionLookup() {
    if (this.correctionAbort) {
      // expected: nobody is waiting on this answer any more
      this.correctionAbort.abort();
      this.correctionAbort = undefined;
    }
  }

  /**
   * Ask the server for `text`, with the fallback timer running beside the request. `generation`
   * is the keystroke this request belongs to; any answer that arrives after a newer keystroke, a
   * cancel or the fallback is ignored.
   */
  private async askServerAsync(text: string, generation: number) {
    const abortController = new AbortController();
    this.abortController = abortController;
    this.fallbackHandle = this.deps.setTimer(() => {
      this.fallbackHandle = undefined;
      // if the server has not answered in time, the word list answers and the request is dropped:
      // moving the generation on is what makes its answer, should it still arrive, be ignored
      if (generation === this.generation) {
        this.generation++;
        this.stopWaiting();
        this.showDeviceRows(text);
      }
    }, SUGGEST_FALLBACK_MS);
    try {
      const rows = await this.deps.requestAsync(text, SUGGEST_ROW_COUNT, abortController.signal);
      if (generation !== this.generation) {
        return;
      }
      this.stopWaiting();
      const list: SuggestionList = { source: SuggestionSource.Server, forText: text, rows };
      // the kept list stays as the server sent it: the spelling-correction step reads its counts
      // and mended flags, which staple rows do not have
      this.lastServerList = list;
      this.deps.onList(this.withStapleRows(list));
    } catch (error) {
      if (generation !== this.generation) {
        return;
      }
      this.stopWaiting();
      // signed out, a passing connection blip and a late answer are not failures worth reporting;
      // anything else is, but the person still gets the word list rather than nothing
      if (isWorthReporting(error)) {
        this.deps.reportFailure(error);
      }
      this.showDeviceRows(text);
    }
  }

  /** The request for the current text is over, one way or another: stop it and stop waiting. */
  private stopWaiting() {
    this.stopRequest();
    this.setWaiting(false);
  }

  /** Clear the fallback timer and abort the request, without saying anything about waiting. */
  private stopRequest() {
    if (this.fallbackHandle !== undefined) {
      this.deps.clearTimer(this.fallbackHandle);
      this.fallbackHandle = undefined;
    }
    if (this.abortController) {
      // expected: the request is no longer wanted; its rejection is ignored by the generation check
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  /** Tell the page whether suggestions are on their way, but only when that changes. */
  private setWaiting(waiting: boolean) {
    if (this.waiting !== waiting) {
      this.waiting = waiting;
      this.deps.onWaitingChange(waiting);
    }
  }

  private showDeviceRows(text: string) {
    this.deps.onList(this.withStapleRows({ source: SuggestionSource.Device, forText: text, rows: this.deps.deviceRows(text) }));
  }

  /** The list with the staple rows for its text on top, when staple suggestions are on. */
  private withStapleRows(list: SuggestionList): SuggestionList {
    const stapleRows = this.deps.stapleRows?.(list.forText) ?? [];
    return stapleRows.length === 0 ? list : { ...list, rows: mergeStapleRows(stapleRows, list.rows) };
  }
}
